import type { App } from 'vue'
import { Store, IErrorReporter, IErrorData, ITenant, IUser, Device, TerminalService } from 'app-core'

import { init, withScope, captureException, captureMessage, Scope } from '@sentry/vue'

import packageJson from '../../package.json'

/**
 * Port of apps/admin/src/wrappers/ErrorReporter.ts onto the Vue 3 Sentry SDK
 * (init({ app }) instead of init({ Vue }), as in apps/sap).
 *
 * Divergence note (intentional fix, logged in tech spec): the old beforeSend spread
 * `this.getErrorData()` — a Promise — into event.tags, so the rich tags never actually
 * landed. Sentry supports async beforeSend; we await the data so tags now arrive.
 * Same data, same release format (`konty-admin@x.y.z`), same ignore list.
 */
class ErrorReporter implements IErrorReporter {

    shouldReportErrors = false

    constructor() {
        this.shouldReportErrors = import.meta.env.VITE_ENV === 'production' && !!import.meta.env.VITE_SENTRY_VUE_DSN
    }

    setUpErrorReporting(app?: App): void {
        if (!this.shouldReportErrors) return

        init({
            app,
            dsn: import.meta.env.VITE_SENTRY_VUE_DSN,
            release: `konty-admin@${packageJson.version}`,
            environment: import.meta.env.VITE_ENV,
            ignoreErrors: [
                'Request failed with status code 401',
                'Request failed with status code 422',
                'The node to be removed is not a child of this node',
                'Error Status 404: App not found',
            ],
            beforeSend: async (event: any): Promise<any> => {
                const errorData = await this.getErrorData()
                event.tags = (!event.tags ? errorData : { ...event.tags, ...errorData }) as { [key: string]: any }
                event.tags.efectiveEnvironment = import.meta.env.VITE_ENV
                return event
            },
        })
    }

    reportError(error: any, data: any = null): void {
        if (!this.shouldReportErrors) return

        withScope((scope: Scope) => {
            if (data) {
                scope.setExtras(data)
            }
            captureException(error)
        })
    }

    reportMessage(message: string, data: any = null): void {
        if (!this.shouldReportErrors) return

        withScope((scope: Scope) => {
            if (data) {
                scope.setExtras(data)
            }
            captureMessage(message)
        })
    }

    async getErrorData(): Promise<IErrorData> {
        const tenant = Store.get<ITenant>('tenant')
        const user = Store.get<IUser>('user')
        const device = Device.get()
        const terminalId = await TerminalService.getId()
        return {
            tenant: tenant?.email || 'none',
            user: user?.name || 'none',
            terminal: terminalId || 'none',
            // Matches the package name and the API context set in main.ts so Sentry's
            // appName tag stays continuous with old admin (audit F19).
            appName: 'admin',
            electronAppRelease: device?.electron?.appVersion || null
        }
    }
}

export const errorReporter = new ErrorReporter()
export default errorReporter
